import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";
import type { SiteConfig } from "@repo/backend/schema/siteConfig";
import type { FeatureFlags, ClientConfig } from "@repo/backend/schema/clientConfig";
import { SectionRenderer } from "./SectionRenderer.js";
import { SiteNav } from "./SiteNav.js";
import { SiteFooter } from "./SiteFooter.js";

gsap.registerPlugin(ScrollTrigger);

interface SiteRouterProps {
  siteConfig: SiteConfig;
  clientId: string;
  businessName: string;
  features?: Partial<FeatureFlags>;
  contact?: ClientConfig["contact"];
}

/**
 * Initialises Lenis smooth scroll synced to GSAP's ticker so ScrollTrigger
 * scrub animations stay frame-perfect. Lenis is opt-in at the site level —
 * wrap SiteRouter in a parent that sets data-no-lenis to disable.
 */
export function useLenis() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });

    // Sync Lenis RAF with GSAP ticker so scrub animations stay accurate
    const onTick = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(onTick);
    gsap.ticker.lagSmoothing(0);

    // Keep ScrollTrigger refresh positions in sync with Lenis scroll
    lenis.on("scroll", ScrollTrigger.update);

    return () => {
      gsap.ticker.remove(onTick);
      lenis.destroy();
    };
  }, []);
}

/**
 * SiteRouter — mounts React Router when routes[] is non-empty, otherwise
 * falls back to a plain single-page render. Lenis smooth scroll is always
 * active; GSAP ScrollTrigger animations are driven by SectionRenderer.
 */
export function SiteRouter({ siteConfig, clientId, businessName, features = {}, contact }: SiteRouterProps) {
  useLenis();

  const { sections, routes, nav, footer } = siteConfig;
  const hasRoutes = routes.length > 0;

  const footerEl = footer?.show !== false ? (
    <SiteFooter
      businessName={businessName}
      footer={footer ?? { show: true, socials: [], links: [] }}
      address={contact?.address ?? undefined}
      phone={contact?.phone ?? undefined}
    />
  ) : null;

  if (!hasRoutes) {
    return (
      <>
        <SectionRenderer sections={sections} clientId={clientId} features={features} />
        {footerEl}
      </>
    );
  }

  const resolvedNav = nav ?? { show: true, position: "top", style: "minimal" as const };
  const showNav = resolvedNav.show;

  return (
    <BrowserRouter>
      {showNav && resolvedNav.position === "top" && (
        <SiteNav businessName={businessName} routes={routes} nav={resolvedNav} />
      )}

      <Routes>
        <Route
          path="/"
          element={
            <SectionRenderer sections={sections} clientId={clientId} features={features} />
          }
        />
        {routes.map((route) => (
          <Route
            key={route.path}
            path={route.path}
            element={
              <SectionRenderer
                sections={route.sections}
                clientId={clientId}
                features={features}
              />
            }
          />
        ))}
        <Route
          path="*"
          element={
            <SectionRenderer sections={sections} clientId={clientId} features={features} />
          }
        />
      </Routes>

      {showNav && resolvedNav.position === "bottom" && (
        <SiteNav businessName={businessName} routes={routes} nav={resolvedNav} />
      )}

      {footerEl}
    </BrowserRouter>
  );
}
